import { Controller } from "stimulus";
import { isMobileDevice } from "../../util";
import Cookies from "js-cookie";
import {
  EventFetchPortfolio,
  EventLoadAddCoinsSection,
  EventLoadCategoriesChips,
  EventLoadChainsChips,
  EventOnboardingNextPage,
  EventOnboardingSelectCoin
} from "../../constants/events";
import { LocalStorage } from "../../util/local_storage";
import { getActiveCurrency } from "../../util/currency";
import { unconditionalTrackEvent } from "../../util/analytics";
import {
  SELECT_NEWSLETTER_SUBSCRIBE_CTA_EVENT,
  SELECT_ONBOARDING_SKIP_EVENT,
} from "../../constants/mixpanel_config";

const MERGE_COINS_STEP = "merge_coins";
const CHOOSE_PATH_STEP = "choose_path";
const DOWNLOAD_APP_STEP = "download_app";
const NEWSLETTER_STEP = "newsletter";
const PREFERENCES_STEP = "preferences";

const SHOW_PREFERENCES_STEP_ONLY = 1;
const HIDE_NEWSLETTER = 3;
const LOGGED_OUT_PORTFOLIO_LOCAL_STORAGE_KEY = "logged_out_portfolio_coins";
const LOP_COINS_MAX_LIMIT = 20;

const ONBOARDING_STEPS = [
  {
    id: MERGE_COINS_STEP,
    analytics_origin: "merge_guest_portfolio",
    index: 0,
    show_skip_button: true
  },
  {
    id: CHOOSE_PATH_STEP,
    analytics_origin: CHOOSE_PATH_STEP,
    index: 1,
    show_skip_button: true
  },
  {
    id: DOWNLOAD_APP_STEP,
    analytics_origin: DOWNLOAD_APP_STEP,
    index: 2,
    show_skip_button: false
  },
  {
    id: NEWSLETTER_STEP,
    analytics_origin: NEWSLETTER_STEP,
    index: 3,
    show_skip_button: true
  },
  {
    id: PREFERENCES_STEP,
    analytics_origin: PREFERENCES_STEP,
    index: 4,
    show_skip_button: false
  }
];

const STEP_INDICES = {
  MERGE_COINS: 0,
  CHOOSE_PATH: 1,
  DOWNLOAD_APP: 2,
  NEWSLETTER: 3,
  PREFERENCES: 4
};

const ONBOARDING_PAGES_MAPPING = ONBOARDING_STEPS.reduce((acc, step) => {
  acc[step.index] = step;
  return acc;
}, {});

export default class extends Controller {
  // ----------------------
  // Controller properties
  // ----------------------
  _availablePages = [];
  _step_1_substep_selection = "add-coins-card";

  _initialized = false;
  _hasLopCoins = false;
  _onAddCoinPage = false;

  _isLoadedAddCoinsSection = false;
  _isLoadedGetRecommendationsChips = false;
  _selectedCoinsOnAddCoinsPage = false;

  static targets = [
    // Toggle modal pages
    "pageContainer", "progressIndicator", "skipButton",

    // Merge LOP coins step
    "mergeCoinsSkeleton", "mergeCoinsSection",

    // Choose a path step
    "addCoinCard", "getRecommendationCard", "addCoinsPage", "getRecommendationPage", "getRecommendationResultsPage",

    // App proposition step
    "webView", "mobileView",

    // Newsletter step
    "newsletterButton",
  ];

  static values = {
    currentPageIndex: Number,
    maxPages: Number,
  }

  // ----------------------
  // Lifecycle methods
  // ----------------------
  async connect() {
    if (Cookies.get("close-user-onboarding")) return;
    if (!this.element) return;

    this._updateTargetAttributes();

    const hasUserInfo = await this._fetchUserInfo();
    if (!hasUserInfo) return;

    await this._fetchLopCoins();
    this._configureOnboardingFlow();
    this._setupUIComponents();
    this._setupEventListeners();
    this._addCloseButtonId();
    this._showModal();

    this._initialized = true;
  }

  currentPageIndexValueChanged() {
    if (!this._initialized) return;

    this._updateUIForCurrentPage();

    this._trackGaEvent("view_onboarding_modal", {
      "onboarding_step": this._currentOnboardingStep(),
      "onboarding_type": this._userOnboardingType(),
    });
  }

  // ----------------------
  // Public methods (UI interactions)
  // ----------------------
  nextPage() {
    const currentIndex = this._availablePages.indexOf(this.currentPageIndexValue);

    if (currentIndex < this._availablePages.length - 1) {
      if (this._onAddCoinPage && this._selectedCoinsOnAddCoinsPage) {
        Modal.confirm(
          I18n.t("user_onboarding.warning.title"),
          I18n.t("user_onboarding.warning.caption_skip_step"),
          I18n.t("user_onboarding.warning.cta.continue_adding_coins"),
          I18n.t("user_onboarding.warning.cta.skip_anyway"),
        ).then((result) => {
          if (result.confirm || result?.source === "click_away") return;
          this._proceedToNextPage(currentIndex);
        });
      } else {
        this._proceedToNextPage(currentIndex);
      }
    } else {
      Modal.hide("coins_user_onboarding");
      this._trackGaEvent("complete_onboarding", {
        "onboarding_type": this._userOnboardingType(),
      });
    }
  }

  chooseStepCard(e) {
    this._step_1_substep_selection = e.currentTarget.dataset.identifier;
  }

  showSubstepPage() {
    if (this._step_1_substep_selection === "add-coins-card") {
      this._showAddCoinPage();
    } else {
      this._showGetRecommendationPage();
    }
  }

  backToFirstPage() {
    this._togglePageVisibility();
  }

  showGetRecommendationsResultPage() {
    this.getRecommendationPageTarget.classList.add("tw-hidden");
    this.getRecommendationResultsPageTarget.classList.remove("tw-hidden");
  }

  backToChipSelectionPage() {
    this.getRecommendationResultsPageTarget.classList.add("tw-hidden");
    this.getRecommendationPageTarget.classList.remove("tw-hidden");
  }

  scrollToDivider(e) {
    e.currentTarget.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "end" });

    let dividerId = e.currentTarget.dataset.divider;
    let scrollToElement = document.getElementById(dividerId);
    setTimeout(() => {
      scrollToElement?.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
    }, 100);

    this._triggerSelectHighlightPillGaEvent(e);
  }

  appCta() {
    this._trackGaEvent("click_mobile_web_to_app_cta", {
      "origin": "onboarding",
      "onboarding_type": this._userOnboardingType(),
    });
  }

  newsletterCta() {
    this._trackGaEvent("click_newsletter_subscribe", {
      "origin": "onboarding",
      "onboarding_type": this._userOnboardingType(),
    });

    unconditionalTrackEvent(SELECT_NEWSLETTER_SUBSCRIBE_CTA_EVENT, {
      "origin": "onboarding_newsletter",
    })
  }

  skipOnboardingStep() {
    this._trackGaEvent("skip_onboarding", {
      "onboarding_step": this._currentOnboardingStep(),
      "onboarding_type": this._userOnboardingType(),
    });

    unconditionalTrackEvent(SELECT_ONBOARDING_SKIP_EVENT, {
      "origin": this._currentOnboardingStep()
    })
  }

  finishOnboarding() {
    this._trackGaEvent("complete_onboarding", {
      "onboarding_type": this._userOnboardingType(),
    });
  }

  // ----------------------
  // Setup and configuration methods
  // ----------------------
  _configureOnboardingFlow() {
    this._availablePages = [];

    if (this._hasLopCoins && this._modalSteps === SHOW_PREFERENCES_STEP_ONLY) {
      this._availablePages = [STEP_INDICES.MERGE_COINS, STEP_INDICES.PREFERENCES];
    }
    else if (this._modalSteps === SHOW_PREFERENCES_STEP_ONLY) {
      this._availablePages = [STEP_INDICES.PREFERENCES];
    }
    else if (this._modalSteps === HIDE_NEWSLETTER) {
      this._availablePages = [
        STEP_INDICES.MERGE_COINS,
        STEP_INDICES.CHOOSE_PATH,
        STEP_INDICES.DOWNLOAD_APP,
        STEP_INDICES.PREFERENCES
      ];

      if (!this._hasLopCoins) {
        this._availablePages = this._availablePages.filter(page => page !== STEP_INDICES.MERGE_COINS);
      }
    }
    else {
      this._availablePages = [
        STEP_INDICES.MERGE_COINS,
        STEP_INDICES.CHOOSE_PATH,
        STEP_INDICES.DOWNLOAD_APP,
        STEP_INDICES.NEWSLETTER,
        STEP_INDICES.PREFERENCES
      ];

      if (!this._hasLopCoins) {
        this._availablePages = this._availablePages.filter(page => page !== STEP_INDICES.MERGE_COINS);
      }
    }

    let startPage = this._availablePages[0] || 0;
    this.maxPagesValue = this._availablePages.length;

    this.currentPageIndexValue = startPage;
    this._removeUnavailablePages();
  }

  _setupUIComponents() {
    this._removeProgressIndicators();
    this._showAppCtaBasedOnView();
    this._updateUIForCurrentPage();
  }

  _setupEventListeners() {
    window.addEventListener(EventOnboardingNextPage, (e) => {
      if (e.detail && e.detail.is_activated_user) {
        const preferencesPageIndex = this._availablePages.indexOf(STEP_INDICES.PREFERENCES);

        if (preferencesPageIndex !== -1) {
          this.currentPageIndexValue = this._availablePages[preferencesPageIndex];
          this._updateUIForCurrentPage();
          return;
        }
      }

      this.nextPage();
    });

    window.addEventListener("gecko-modal-close", (e) => {
      if (e.detail.modalId === "coins_user_onboarding") {
        Cookies.set("close-user-onboarding", true, { expires: 365 });
        window.dispatchEvent(new CustomEvent(EventFetchPortfolio));
      }
    });

    window.addEventListener(EventOnboardingSelectCoin, (e) => {
      this._selectedCoinsOnAddCoinsPage = e.detail.hasCoins;
    });
  }

  _addCloseButtonId() {
    const closeButton = this.element.querySelector('[\\@click="coins_user_onboarding = false"]');
    closeButton.setAttribute("id", `onboarding-close`);

    this._addCloseButtonEventListener();
  }

  _addCloseButtonEventListener() {
    const closeButton = document.getElementById("onboarding-close");

    if (closeButton) {
      closeButton.addEventListener("click", () => {
        this._trackGaEvent("quit_onboarding", {
          "onboarding_step": this._currentOnboardingStep(),
          "onboarding_type": this._userOnboardingType(),
        });
      });
    }
  }

  _updateTargetAttributes() {
    // Find all elements with data-user-onboarding-target within this controller's element
    const oldTargets = this.element.querySelectorAll('[data-user-onboarding-target]');

    // Update each element's target attribute to use the v2 controller
    oldTargets.forEach(element => {
      const targetName = element.getAttribute('data-user-onboarding-target');
      element.setAttribute('data-user-onboarding-v2-target', targetName);
      element.removeAttribute('data-user-onboarding-target');
    });

    // Also update action attributes if needed
    const oldActions = this.element.querySelectorAll('[data-action*="user-onboarding#"]');

    oldActions.forEach(element => {
      const actions = element.getAttribute('data-action');
      const updatedActions = actions.replace(/user-onboarding#/g, 'user-onboarding-v2#');
      element.setAttribute('data-action', updatedActions);
    });
  }

  // ----------------------
  // Data fetching methods
  // ----------------------
  async _fetchUserInfo() {
    try {
      const response = await fetch(`/onboarding/user_info`);
      if (!response.ok) return false;

      const userInfo = await response.json();
      if (!userInfo.show_modal) return false;

      this._userEmail = userInfo.email;
      this._modalSteps = userInfo.steps;
      this._newUser = userInfo.new_user;

      this.newsletterButtonTarget.setAttribute("data-email", this._userEmail);
      return true;
    } catch {
      return false;
    }
  }

  async _fetchLopCoins() {
    const localStorage = LocalStorage.initStorage(LOGGED_OUT_PORTFOLIO_LOCAL_STORAGE_KEY, []);
    const lopCoins = localStorage.get();
    const cleanedLopCoins = this._cleanupLocalStorageArray(lopCoins, LOP_COINS_MAX_LIMIT);

    if (Array.isArray(lopCoins) && lopCoins.length > 0) {
      this._hasLopCoins = true;

      const params = new URLSearchParams();
      params.set("coin_ids", cleanedLopCoins.join(','));
      params.set("vs_currency", getActiveCurrency());

      const url = `/${I18n.locale}/onboarding/merge_coins_section?${params}`;
      await fetch(url, { credentials: "same-origin", })
        .then(async (response) => {
          if (!response.ok) {
            Toaster.toast("An unknown error occurred", {
              icon: {
                classes: "fas fa-times tw-text-danger-500"
              }
            });
          } else {
            this.mergeCoinsSectionTarget.innerHTML = await response.text();
            this.mergeCoinsSkeletonTarget.classList.add("tw-hidden");
            this.mergeCoinsSectionTarget.classList.remove("tw-hidden");
          }
        });
    }
  }

  // ----------------------
  // UI update methods
  // ----------------------
  _updateUIForCurrentPage() {
    this._togglePageVisibility();
    this._toggleActiveStep();
    this._toggleSkipButton();
  }

  _togglePageVisibility() {
    this._hideSubstepPages();

    const currentPage = this.currentPageIndexValue;
    const currentPageName = ONBOARDING_PAGES_MAPPING[currentPage].id;

    this.pageContainerTargets.forEach(container => {
      container.classList.add("tw-hidden");
    });

    const pageToShow = this.pageContainerTargets.find(container => {
      return container.id && container.id.includes(currentPageName);
    });

    if (pageToShow) {
      pageToShow.classList.remove("tw-hidden");
    }
  }

  _hideSubstepPages() {
    this.addCoinsPageTarget.classList.add("tw-hidden");
    this.getRecommendationPageTarget.classList.add("tw-hidden");
    this.getRecommendationResultsPageTarget.classList.add("tw-hidden");
  }

  _toggleActiveStep() {
    const currentStepIndex = this._availablePages.indexOf(this.currentPageIndexValue);

    this.progressIndicatorTargets.forEach((step, i) => {
      step.classList.toggle("active", i === currentStepIndex);
    });
  }

  _toggleSkipButton() {
    const currentPage = this.currentPageIndexValue;
    const showSkipButton = ONBOARDING_PAGES_MAPPING[currentPage]?.show_skip_button || false;

    this.skipButtonTarget.classList.toggle("tw-hidden", !showSkipButton);
  }

  _showAppCtaBasedOnView() {
    if (!this.hasMobileViewTarget || !this.hasWebViewTarget) return;

    if (isMobileDevice(navigator.userAgent)) {
      this.mobileViewTarget.classList.remove("tw-hidden");
      this.mobileViewTarget.classList.add("tw-flex");
    } else {
      this.webViewTarget.classList.remove("tw-hidden");
      this.webViewTarget.classList.add("tw-flex");
    }
  }

  _removeUnavailablePages() {
    const allPageIndices = [
      STEP_INDICES.MERGE_COINS,
      STEP_INDICES.CHOOSE_PATH,
      STEP_INDICES.DOWNLOAD_APP,
      STEP_INDICES.NEWSLETTER,
      STEP_INDICES.PREFERENCES
    ];

    const pagesToHide = allPageIndices.filter(index => !this._availablePages.includes(index));

    pagesToHide.forEach(pageIndex => {
      const pageId = `onboarding-${ONBOARDING_PAGES_MAPPING[pageIndex].id}`;
      const element = document.getElementById(pageId);
      if (element) {
        element.remove();
      }
    });

    this._removeProgressIndicators();
  }

  _removeProgressIndicators() {
    const indicators = this.progressIndicatorTargets;

    for (let i = indicators.length - 1; i >= 0; i--) {
      if (i >= this._availablePages.length) {
        indicators[i].remove();
      }
    }
  }

  // ----------------------
  // Navigation methods
  // ----------------------
  _showModal() {
    Modal.show("coins_user_onboarding");
    this._trackGaEvent("start_onboarding");
  }

  _proceedToNextPage(currentIndex) {
    this._onAddCoinPage = false;
    this.currentPageIndexValue = this._availablePages[currentIndex + 1];
  }

  _showAddCoinPage() {
    this._onAddCoinPage = true;

    this._trackGaEvent("view_onboarding_modal", {
      "onboarding_step": this._currentOnboardingStep(),
      "onboarding_type": this._userOnboardingType(),
    });

    this._trackGaEvent("click_onboarding_portfolio_next", {
      "onboarding_step": "choose_path",
      "onboarding_type": this._userOnboardingType(),
      "select_option": "add_coin"
    });

    if (!this._isLoadedAddCoinsSection) {
      this._isLoadedAddCoinsSection = true;
      window.dispatchEvent(new CustomEvent(EventLoadAddCoinsSection));
    }

    this.addCoinsPageTarget.classList.remove("tw-hidden");
    this.pageContainerTargets.find(container =>
      container.id && container.id.includes(CHOOSE_PATH_STEP)
    )?.classList.add("tw-hidden");
  }

  _showGetRecommendationPage() {
    if (!this._isLoadedGetRecommendationsChips) {
      this._isLoadedGetRecommendationsChips = true;

      window.dispatchEvent(new CustomEvent(EventLoadChainsChips));
      window.dispatchEvent(new CustomEvent(EventLoadCategoriesChips));
    }

    this.getRecommendationPageTarget.classList.remove("tw-hidden");
    this.pageContainerTargets.find(container =>
      container.id && container.id.includes(CHOOSE_PATH_STEP)
    )?.classList.add("tw-hidden");
  }

  // ----------------------
  // Helper/Utility methods
  // ----------------------
  _userOnboardingType() {
    return this._newUser ? "new" : "continuous";
  }

  _currentOnboardingStep() {
    if (this._onAddCoinPage) { return "add_coins"; }

    const currentPage = this.currentPageIndexValue;
    return ONBOARDING_PAGES_MAPPING[currentPage]?.analytics_origin || "";
  }

  _triggerSelectHighlightPillGaEvent(e) {
    let selectHighlightOption = e.currentTarget.dataset.option;

    this._trackGaEvent("click_add_coins_highlight", {
      "origin": "onboarding",
      "onboarding_type": this._userOnboardingType(),
      "select_highlight_option": selectHighlightOption
    });
  }

  _trackGaEvent(eventName, eventParams = {}) {
    if (typeof gtag === 'undefined') return;

    gtag("event", eventName, eventParams);
  }

  _cleanupLocalStorageArray(rawArray, limit = LOP_COINS_MAX_LIMIT) {
    if (!rawArray || !Array.isArray(rawArray)) {
      return [];
    }

    const cleanedArray = rawArray
      .map(value => {
        if (typeof value === 'number') {
          return Number.isInteger(value) ? value : null;
        }

        if (typeof value === 'string') {
          const parsed = parseInt(value, 10);
          return !isNaN(parsed) ? parsed : null;
        }

        return null;
      })
      .filter(value => value !== null);

    return cleanedArray.slice(0, limit);
  }
}
